import { apiSlice } from '../api/apiSlice'

export type FeedCardType =
  | 'product'
  | 'product_video'
  | 'creator_products'
  | 'essentials'
  | 'article'
  | 'event'
  | 'space'
export type DiscoverCardBody = {
  title: string
  type: FeedCardType
  sourceId: string
  storefrontId: string
  productIds: string[]
  interestIds: string[]
  videoId?: string | null
  centreId?: string | null
  status: 'draft' | 'published'
  startsAt?: string | null
  endsAt?: string | null
}
export type CardProductSummary = { _id: string; name: string }
export type DiscoverCardPreviewData = {
  type: FeedCardType
  title?: string
  description?: string
  image?: string
  creator?: { _id: string; name: string; logo?: string }
  centre?: { _id: string; name: string; address?: string }
  video?: { title: string; thumbnailUrl: string; watchUrl: string }
  products: {
    _id: string
    name: string
    images: string[]
    price: number
    eventDate?: string
  }[]
}
export type DiscoverCardPreviewBody = Pick<
  DiscoverCardBody,
  'type' | 'sourceId' | 'productIds' | 'videoId' | 'centreId'
>
export type AdminDiscoverCard = DiscoverCardBody & {
  _id: string
  updatedAt: string
  displayOrder?: number
  productSummaries: CardProductSummary[]
  preview: DiscoverCardPreviewData
}
export type CardSourceOption = {
  _id: string
  name?: string
  title?: string
  images?: string[]
  banner?: string
  bannerUrl?: string
  vendor?: string
  price?: number
  address?: string
}
type Envelope<T> = { success: boolean; data: T }
type CardPage = {
  cards: AdminDiscoverCard[]
  total: number
  skip: number
  limit: number
}

export const discoverCardApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDiscoverFeedCardPreview: builder.query<
      DiscoverCardPreviewData,
      DiscoverCardPreviewBody
    >({
      query: (body) => ({
        url: 'v2/discover/card-preview',
        method: 'POST',
        body,
      }),
      transformResponse: (response: Envelope<DiscoverCardPreviewData>) =>
        response.data,
      providesTags: ['DISCOVER_CARD'],
    }),
    getDiscoverFeedCards: builder.query<
      CardPage,
      { skip: number; storefrontId?: string }
    >({
      query: (params) => ({ url: 'v2/discover/cards', params }),
      transformResponse: (response: Envelope<CardPage>) => response.data,
      providesTags: ['DISCOVER_CARD'],
    }),
    getStorefrontDiscoverCards: builder.query<AdminDiscoverCard[], string>({
      async queryFn(storefrontId, _api, _extraOptions, fetchWithBaseQuery) {
        const cards: AdminDiscoverCard[] = []
        let skip = 0
        let total = 0
        do {
          const response = await fetchWithBaseQuery({
            url: 'v2/discover/cards',
            params: { storefrontId, skip, limit: 100 },
          })
          if (response.error) return { error: response.error }
          const page = (response.data as Envelope<CardPage>).data
          cards.push(...page.cards)
          skip += page.cards.length
          total = page.total
          if (page.cards.length === 0 && skip < total) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'Unable to load the full card list. Please retry.',
              },
            }
          }
        } while (skip < total)
        return { data: cards }
      },
      providesTags: ['DISCOVER_CARD'],
    }),
    reorderDiscoverCards: builder.mutation<
      Envelope<{ cardIds: string[] }>,
      { storefrontId: string; cardIds: string[] }
    >({
      query: (body) => ({
        url: 'v2/discover/cards/order',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['DISCOVER_CARD'],
    }),
    saveDiscoverCard: builder.mutation<
      Envelope<AdminDiscoverCard>,
      { id?: string; body: DiscoverCardBody }
    >({
      query: ({ id, body }) => ({
        url: `v2/discover/cards${id === undefined ? '' : `/${id}`}`,
        method: id === undefined ? 'POST' : 'PUT',
        body,
      }),
      invalidatesTags: ['DISCOVER_CARD'],
    }),
    hideDiscoverCard: builder.mutation<Envelope<AdminDiscoverCard>, string>({
      query: (id) => ({ url: `v2/discover/cards/${id}/hide`, method: 'PUT' }),
      invalidatesTags: ['DISCOVER_CARD'],
    }),
    deleteDiscoverCard: builder.mutation<Envelope<unknown>, string>({
      query: (id) => ({ url: `v2/discover/cards/${id}`, method: 'DELETE' }),
      invalidatesTags: ['DISCOVER_CARD'],
    }),
    getDiscoverCardOptions: builder.query<
      CardSourceOption[],
      { type: FeedCardType; search?: string; selectedId?: string }
    >({
      query: (params) => ({ url: 'v2/discover/card-options', params }),
      transformResponse: (response: Envelope<CardSourceOption[]>) =>
        response.data,
    }),
  }),
})
export const {
  useGetDiscoverFeedCardsQuery,
  useGetStorefrontDiscoverCardsQuery,
  useReorderDiscoverCardsMutation,
  useGetDiscoverFeedCardPreviewQuery,
  useSaveDiscoverCardMutation,
  useHideDiscoverCardMutation,
  useDeleteDiscoverCardMutation,
  useGetDiscoverCardOptionsQuery,
} = discoverCardApi
